/**
 * Aspect — Room builder
 * --------------------------------------------------------------
 * Builds static room geometry from a serialized room map: a meter-tiled floor
 * (wood, with tile overlays for wet rooms), plaster walls split around door and
 * window openings, door/window frames, transparent window
 * glass, and a soft ceiling. Returns a single root entity so the whole room can
 * be torn down and rebuilt on room changes without leaking.
 *
 * Materials come from surface_library.js — ONE shared material per surface type
 * per build (engine rule). The ceiling + glass materials are exposed on the root
 * so the wall-cutaway system can fade them in the overhead "dollhouse" view.
 *
 * Maps in this MVP are axis-aligned rectangles; walls running along X or Z are
 * sized directly (no rotation) which sidesteps yaw-sign ambiguity. Oblique walls
 * fall back to a computed yaw.
 */

import * as pc from "playcanvas";
import { createSurfaceLibrary, createGrassMaterial } from "./surface_library.js";
import { ensureBalconyViewWalls, renderWallMeshes } from "./wall_render.js";
import { wallLength } from "./wall_geometry.js";
import { DEFAULT_FLOOR_COUNT, DEFAULT_ACTIVE_FLOOR } from "./floor_defaults.js";
import { createFloorSlabEntity, createPolygonFloorEntity } from "./floor_mesh.js";
import { installSeamSeals } from "./seam_seals.js";
import { isWetFloorRoom } from "./structure_pricing.js";

const AXIS_EPS = 1e-3;
const MIN_SEG = 0.02; // don't spawn slivers smaller than this (meters)
const BASEBOARD_H = 0.1;
const FRAME_W = 0.06; // along-wall width of a door/window frame member
export const CEIL_SLAB = 0.08;
/** Inactive storeys above the active floor start above its ceiling slab (balcony z-fight fix). */
const SHELL_STOREY_GAP = 0.12;
/** Floor and ceiling bury through wall thickness on every side — no sky / void slits. */
export const SLAB_WRAP = 0.32;
export const SLAB_OVERSIZE = SLAB_WRAP;
export const CEIL_OVERSIZE = SLAB_WRAP;
/** Wet-room overlay sits above the wood top (floorY) so marble never z-fights planks. */
const WET_FLOOR_THICK = 0.014;
const WET_FLOOR_TOP = 0.012;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/** Axis-aligned slab that overshoots the envelope on every side. */
function envelopeSlab(min, max, oversize = SLAB_WRAP) {
  const pad = Number(oversize) || SLAB_WRAP;
  const x0 = min[0] - pad;
  const x1 = max[0] + pad;
  const z0 = min[2] - pad;
  const z1 = max[2] + pad;
  return {
    w: Math.max(0.1, x1 - x0),
    d: Math.max(0.1, z1 - z0),
    cx: (x0 + x1) / 2,
    cz: (z0 + z1) / 2,
  };
}

/** Wood floor: same wrap as the ceiling so neither slab leaves a see-through gap. */
export function floorEnvelopeSlab(min, max) {
  return envelopeSlab(min, max, SLAB_WRAP);
}

/** Ceiling/roof: fully covers the floor footprint and wall mass. */
export function ceilingEnvelopeSlab(min, max) {
  return envelopeSlab(min, max, SLAB_WRAP);
}

/** Marble / tile slab on bathrooms (and other wet rooms), clearly above the wood. */
function addWetRoomFloors(root, room, min, floorY, surf, app) {
  for (const r of room.rooms ?? []) {
    if (!isWetFloorRoom(r) && surf.floorForType(r.type) !== surf.floorTile) continue;
    const name = `floor-tile-${r.name ?? r.type ?? "wet"}`;
    const meta = {
      name: r.name ?? r.type ?? "Wet room",
      type: r.type ?? "wet",
      x: Number(r.x) || 0,
      z: Number(r.z) || 0,
      width: Number(r.width) || 0,
      depth: Number(r.depth) || 0,
    };
    const top = floorY + WET_FLOOR_TOP;
    let tile = null;
    if (Array.isArray(r.polygon) && r.polygon.length >= 3 && app) {
      tile = createPolygonFloorEntity(app, name, r.polygon, top, surf.floorTile, WET_FLOOR_THICK);
    }
    if (!tile) {
      const rw = Number(r.width) || 0;
      const rd = Number(r.depth) || 0;
      if (rw < 0.3 || rd < 0.3) continue;
      tile = boxEntity(name, [rw, WET_FLOOR_THICK, rd], surf.floorTile, false, true);
      tile.setLocalPosition(
        min[0] + (Number(r.x) || 0) + rw / 2,
        top - WET_FLOOR_THICK / 2,
        min[2] + (Number(r.z) || 0) + rd / 2,
      );
    }
    tile._aspectKind = "floor";
    tile._aspectRoom = meta;
    root.addChild(tile);
  }
}

/** Window/door vertical placement in the active storey's local wall frame. */
function openingVertical(op, floorY) {
  if (op.type !== "window") {
    const h = op.height ?? 2.1;
    return { bottom: 0, top: h, cy: floorY + h / 2, sill: 0 };
  }
  const h = op.height ?? 1.2;
  const sill = op.sill != null ? op.sill : 0.9;
  return { bottom: sill, top: sill + h, cy: floorY + sill + h / 2, sill };
}

function boxEntity(name, size, material, castShadows = true, receiveShadows = true) {
  const e = new pc.Entity(name);
  e.addComponent("render", { type: "box", material, castShadows, receiveShadows });
  e.setLocalScale(size[0], size[1], size[2]);
  return e;
}

/** Drop collinear walls that share the same segment (saved plans can duplicate edges). */
function dedupeWalls(walls) {
  const eps = 0.02;
  const lineEps = 0.08;
  const snap = (v) => Math.round(Number(v) / eps) * eps;

  const exactKey = (w) => {
    const a = `${snap(w.start[0])},${snap(w.start[1])}`;
    const b = `${snap(w.end[0])},${snap(w.end[1])}`;
    return a < b ? `${a}|${b}` : `${b}|${a}`;
  };

  /** Axis-aligned walls on the same line within ~wall thickness. */
  const lineKey = (w) => {
    const ax = w.start[0];
    const az = w.start[1];
    const bx = w.end[0];
    const bz = w.end[1];
    if (Math.abs(az - bz) < lineEps) {
      const z = snap((az + bz) / 2);
      const x0 = snap(Math.min(ax, bx));
      const x1 = snap(Math.max(ax, bx));
      return `h|${z}|${x0}|${x1}`;
    }
    if (Math.abs(ax - bx) < lineEps) {
      const x = snap((ax + bx) / 2);
      const z0 = snap(Math.min(az, bz));
      const z1 = snap(Math.max(az, bz));
      return `v|${x}|${z0}|${z1}`;
    }
    return exactKey(w);
  };

  const seenExact = new Set();
  const byLine = new Map();
  const out = [];

  for (const w of walls ?? []) {
    const ek = exactKey(w);
    if (seenExact.has(ek)) continue;
    seenExact.add(ek);

    const lk = lineKey(w);
    const prev = byLine.get(lk);
    if (prev) {
      const prevLen = wallLength(prev);
      const len = wallLength(w);
      if (len <= prevLen + eps) continue;
      const idx = out.indexOf(prev);
      if (idx >= 0) out.splice(idx, 1);
    }
    byLine.set(lk, w);
    out.push(w);
  }
  return out;
}

/** Solid perimeter walls on all four sides — placeholder storeys above/below the active floor. */
function buildFloorShell(root, min, max, shellY, ceilH, surf, skipSides = null) {
  const THICK = 0.15;
  const half = THICK / 2;
  const skip = skipSides ?? new Set();
  const x0 = min[0];
  const x1 = max[0];
  const z0 = min[2];
  const z1 = max[2];
  const w = x1 - x0;
  const d = z1 - z0;
  const cx = (x0 + x1) / 2;
  const cz = (z0 + z1) / 2;
  const yMid = shellY + ceilH / 2;
  const add = (name, size, px, py, pz) => {
    const e = boxEntity(name, size, surf.wall, true, true);
    e.setLocalPosition(px, py, pz);
    root.addChild(e);
  };
  if (!skip.has("south")) add("shell-s", [w, ceilH, THICK], cx, yMid, z0 - half);
  if (!skip.has("north")) add("shell-n", [w, ceilH, THICK], cx, yMid, z1 + half);
  if (!skip.has("west")) add("shell-w", [THICK, ceilH, d], x0 - half, yMid, cz);
  if (!skip.has("east")) add("shell-e", [THICK, ceilH, d], x1 + half, yMid, cz);
}

/** Y base for an inactive storey shell (matches buildRoom loop). */
function shellBaseY(floorIndex, ceilH, activeFloor) {
  let shellY = (floorIndex - 1) * ceilH;
  if (floorIndex > activeFloor) shellY += CEIL_SLAB + SHELL_STOREY_GAP;
  return shellY;
}

/** Roof/ceiling slab capping the top inactive storey (multi-floor dollhouse view). */
function buildShellRoof(root, min, max, shellY, ceilH, surf) {
  const slab = ceilingEnvelopeSlab(min, max);
  // Casts shadows so the sun can't pour straight through the building top.
  const roof = boxEntity("building-roof", [slab.w, CEIL_SLAB, slab.d], surf.ceiling, true, false);
  roof.setLocalPosition(slab.cx, shellY + ceilH + CEIL_SLAB / 2, slab.cz);
  roof._aspectKind = "ceiling";
  root.addChild(roof);
}

/** Map an axis-aligned perimeter wall to south/north/west/east (balcony view walls). */
function wallEnvelopeSide(w, min, max) {
  const ax = w.start[0];
  const az = w.start[1];
  const bx = w.end[0];
  const bz = w.end[1];
  if (Math.abs(az - bz) < AXIS_EPS) {
    if (Math.abs(az - min[2]) < AXIS_EPS) return "south";
    if (Math.abs(az - max[2]) < AXIS_EPS) return "north";
  }
  if (Math.abs(ax - bx) < AXIS_EPS) {
    if (Math.abs(ax - min[0]) < AXIS_EPS) return "west";
    if (Math.abs(ax - max[0]) < AXIS_EPS) return "east";
  }
  return null;
}

/**
 * @param {pc.AppBase} app
 * @param {object} room serialized room (bounds, walls, openings, floorY, rooms?)
 * @param {object} [shared] { skyTexture } reused across rebuilds
 * @returns {pc.Entity} room root
 */
export function buildRoom(app, room, shared = {}) {
  const root = new pc.Entity("room-root");
  const floorCount = clamp(Math.round(Number(room.floorCount) || DEFAULT_FLOOR_COUNT), 1, 10);
  const activeFloor = clamp(Math.round(Number(room.activeFloor) || DEFAULT_ACTIVE_FLOOR), 1, floorCount);
  const ceilH = room.dimensions?.height ?? room.bounds?.max?.[1] - room.bounds?.min?.[1] ?? 2.8;
  const floorY = room.floorY ?? (activeFloor - 1) * ceilH;
  const { min, max } = room.bounds;

  const width = max[0] - min[0];
  const depth = max[2] - min[2];
  const floorOnly = !(room.walls?.length);

  const surf = createSurfaceLibrary(app, {
    width,
    depth,
    height: ceilH,
    skyTexture: shared.skyTexture ?? null,
    finishes: room.finishes ?? {},
  });

  const viewSides = new Set();
  for (const w of room.walls ?? []) {
    if (!w.view) continue;
    const side = wallEnvelopeSide(w, min, max);
    if (side) viewSides.add(side);
  }

  // Other storeys: hollow perimeter shells (walls on all four sides only).
  if (floorCount > 1 && !floorOnly) {
    for (let i = 1; i <= floorCount; i++) {
      if (i === activeFloor) continue;
      const shellY = shellBaseY(i, ceilH, activeFloor);
      const skip = i > activeFloor ? viewSides : null;
      buildFloorShell(root, min, max, shellY, ceilH, surf, skip);
      if (i === floorCount) buildShellRoof(root, min, max, shellY, ceilH, surf);
    }
  }

  const cx0 = (min[0] + max[0]) / 2;
  const cz0 = (min[2] + max[2]) / 2;

  // --- Grass ground (fills the empty void around the home on every side) ---
  // A large plane centered on the envelope, sized to the footprint plus a wide
  // margin so the building reads as sitting on open ground. One shared material
  // per build; it receives shadows but never casts them, and uses its own
  // material reference so the wall-cutaway fade logic leaves it untouched.
  const GRASS_MARGIN = 5; // meters of ground beyond the footprint on each side
  const GRASS_THICK = 0.08;
  // Well below the wood slab (top ≈ floorY-0.18) so balcony ledges and exterior
  // door overshoot never z-fight the lawn.
  const GRASS_Y = -0.22;
  const grassW = width + GRASS_MARGIN * 2;
  const grassD = depth + GRASS_MARGIN * 2;
  const grass = createGrassMaterial(app, grassW, grassD);
  const ground = boxEntity("grass-ground", [grassW, GRASS_THICK, grassD], grass, false, true);
  ground.setLocalPosition(cx0, GRASS_Y, cz0);
  root.addChild(ground);

  // --- Floor (envelope wood base + per-room marble/tile overlays for wet rooms) ---
  // Wrap past every wall — including balcony sides — so you cannot see under
  // the slab. Coping sits on top of this extra wood; that is better than a void.
  const floorSlab = floorEnvelopeSlab(min, max);
  let floor;
  if (floorOnly) {
    floor = createFloorSlabEntity(app, "floor", room, floorY, surf.floorWood);
    if (floor) root.addChild(floor);
  } else {
    floor = boxEntity("floor", [floorSlab.w, 0.05, floorSlab.d], surf.floorWood, false, true);
    floor.setLocalPosition(floorSlab.cx, floorY - 0.025, floorSlab.cz);
    floor._aspectKind = "floor";
    root.addChild(floor);
  }

  addWetRoomFloors(root, room, min, floorY, surf, app);

  // --- Ceiling (hidden when the apartment was cleared — floor-only slate) ---
  if (!floorOnly) {
    // Always larger than the floor and wall envelope — never inherit the
    // balcony floor inset or a sky slit opens between plaster and gypsum.
    const ceilSlab = ceilingEnvelopeSlab(min, max);
    const ceiling = boxEntity("ceiling", [ceilSlab.w, CEIL_SLAB, ceilSlab.d], surf.ceiling, true, false);
    ceiling.setLocalPosition(ceilSlab.cx, floorY + ceilH + CEIL_SLAB / 2, ceilSlab.cz);
    ceiling._aspectKind = "ceiling";
    root.addChild(ceiling);
  }

  // --- Walls (with door/window gaps cut out; arcs tessellated) ---
  ensureBalconyViewWalls(room);
  for (const w of dedupeWalls(room.walls)) {
    if (wallLength(w) < AXIS_EPS) continue;
    renderWallMeshes(app, room, root, w, surf, shared);
  }

  // Shadow-casting joint plugs — survive baked shell swap (not tagged as wall/floor).
  if (!floorOnly) installSeamSeals(root, room, surf);

  app.root.addChild(root);
  root._aspectWallMat = surf.wall;
  root._aspectCeilingMat = surf.ceiling;
  root._aspectFloorMat = surf.floorWood;
  // Whole library cached so incremental wall rebuilds reuse the SAME shared
  // materials (engine rule: one material per surface type per build) — rebuilt
  // walls keep fading correctly in the overhead cutaway.
  root._aspectSurf = surf;
  return root;
}
